import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Button, 
  Modal, 
  Form, 
  Input, 
  Select, 
  Radio, 
  Checkbox, 
  Space, 
  Table, 
  Tag, 
  message, 
  Popconfirm,
  DatePicker,
  Row,
  Col,
  Divider,
  Typography,
  Empty,
  Spin
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  EyeOutlined,
  CopyOutlined,
  FilterOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import './FeedbackForms.css';
import axios from 'axios';
import { BaseUrl } from '../Constants/Constants';
import moment from 'moment';
import { FeedbackFormsAPI, FormTypes, DefaultQuestions } from './FeedbackFormsAPI';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const FeedbackForms = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [previewForm] = Form.useForm();
  
  // State management
  const [forms, setForms] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [previewModalVisible, setPreviewModalVisible] = useState(false);
  const [assignmentModalVisible, setAssignmentModalVisible] = useState(false);
  const [reportsModalVisible, setReportsModalVisible] = useState(false);
  const [editingForm, setEditingForm] = useState(null);
  const [formType, setFormType] = useState('default');
  const [questions, setQuestions] = useState([]);
  const [selectedForm, setSelectedForm] = useState(null);
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [courseReports, setCourseReports] = useState([]);
  const [filters, setFilters] = useState({
    course: null,
    formType: null,
    dateRange: null
  });

  // Form types configuration - Updated to match API
  const formTypes = [
    { value: 'default', label: 'Default Form', description: 'Predefined standard feedback questions' },
    { value: 'multiple_choice', label: 'Multiple-Choice Custom Form', description: 'Create custom questions with multiple-choice options' },
    { value: 'yes_no', label: 'Yes/No Form', description: 'Create feedback forms with Yes/No type questions' }
  ];

  // Use imported default questions
  const defaultQuestions = DefaultQuestions;

  // Table columns
  const columns = [
    {
      title: 'Assigned Course',
      dataIndex: 'assignedCourses',
      key: 'assignedCourses',
      render: (_, record) => {
        if (record.courseId) {
          const course = courses.find(c => c.course_id === record.courseId);
          return course?.course_title || `Course ${record.courseId}`;
        }
        return 'Not assigned';
      },
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (type) => {
        const typeConfig = formTypes.find(ft => ft.value === type);
        const colors = {
          'default': 'blue',
          'multiple-choice': 'green',
          'yes-no': 'orange'
        };
        return <Tag color={colors[type]}>{typeConfig?.label}</Tag>;
      },
    },
    {
      title: 'Questions',
      dataIndex: 'questions',
      key: 'questions',
      render: (questions) => `${questions?.length || 0} questions`,
    },
   
    {
      title: 'Form Name',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <div>
          <Text strong>{text}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {formTypes.find(ft => ft.value === record.type)?.label}
          </Text>
        </div>
      ),
    },
    {
      title: 'Created Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => moment(date).format('MMM DD, YYYY'),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => handlePreview(record)}
            title="Preview"
          />
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            title="Edit"
          />
          {/* <Button
            type="text"
            icon={<CopyOutlined />}
            onClick={() => handleDuplicate(record)}
            title="Duplicate"
          /> */}
          <Popconfirm
            title="Are you sure you want to delete this form?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              title="Delete"
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Load data on component mount
  useEffect(() => {
    console.log('Component mounted, loading forms and courses...');
    loadForms();
    loadCourses();
  }, []);

  const loadForms = async () => {
    setLoading(true);
    try {
      const response = await FeedbackFormsAPI.getAllForms();
      console.log('API Response:', response); // Debug log
      
      // Handle both 200 and 304 status codes, and direct array response
      if ((response.status === 'Ok' && response.statuscode === 200) || 
          response.statuscode === 304 || 
          Array.isArray(response)) {
        
        // Handle direct array response or nested data
        const formsData = Array.isArray(response) ? response : response.data;
        
        // Transform API data to match component structure
        const transformedForms = formsData.map(form => {
          let parsedQuestions = [];
          try {
            parsedQuestions = JSON.parse(form.form_questions || '[]');
            // Ensure it's an array
            if (!Array.isArray(parsedQuestions)) {
              parsedQuestions = [];
            }
          } catch (e) {
            console.error('Error parsing form questions:', e);
            parsedQuestions = [];
          }
          
          return {
            id: form.feedback_form_id,
            name: form.form_title,
            type: form.form_type,
            questions: parsedQuestions,
            assignedCourses: form.course_id ? [form.course_id] : [],
            createdAt: new Date(form.created_at),
            status: form.is_active ? 'active' : 'inactive',
            createdBy: form.created_by,
            courseId: form.course_id
          };
        });
        
        console.log('Transformed Forms:', transformedForms); // Debug log
        setForms(transformedForms);
        console.log('Forms state updated with', transformedForms.length, 'forms');
      } else {
        message.error(response.message || 'Failed to load feedback forms');
      }
    } catch (error) {
      console.error('Error loading feedback forms:', error);
      message.error('Failed to load feedback forms. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  };

  const loadCourses = async () => {
    try {
      const response = await axios.get(`${BaseUrl}courses/findAll`, { withCredentials: true });
      setCourses(response.data.data || []);
    } catch (error) {
      console.error('Error loading courses:', error);
      message.error('Failed to load courses. Some features may not work properly.');
    }
  };

  const handleCreateForm = () => {
    setEditingForm(null);
    setFormType('default');
    setQuestions(defaultQuestions);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (formData) => {
    setEditingForm(formData);
    setFormType(formData.type);
    setQuestions(formData.questions || []);
    form.setFieldsValue({
      name: formData.name,
      description: formData.description,
      course_id: formData.courseId || formData.assignedCourses?.[0] || null
    });
    setModalVisible(true);
  };

  const handleDuplicate = (formData) => {
    const duplicatedForm = {
      ...formData,
      name: `${formData.name} (Copy)`,
      id: Date.now() // Temporary ID
    };
    setEditingForm(duplicatedForm);
    setFormType(formData.type);
    setQuestions(formData.questions || []);
    form.setFieldsValue({
      name: duplicatedForm.name,
      description: duplicatedForm.description,
      course_id: null // Reset course selection for duplicate
    });
    setModalVisible(true);
  };

  const handleDelete = async (formId) => {
    try {
      // Get user information from localStorage
      const profileData = localStorage.getItem('Profile');
      const modifiedBy = profileData ? JSON.parse(profileData).email || JSON.parse(profileData).username : undefined;
      
      const response = await FeedbackFormsAPI.deleteForm(formId, modifiedBy);
      if (response.status === 'Ok' && response.statuscode === 200) {
        message.success('Feedback form deleted successfully');
        loadForms(); // Reload forms from API
      } else {
        message.error(response.message || 'Failed to delete feedback form');
      }
    } catch (error) {
      console.error('Error deleting feedback form:', error);
      message.error('Failed to delete feedback form. Please try again.');
    }
  };

  const handlePreview = (formData) => {
    setSelectedForm(formData);
    setPreviewModalVisible(true);
  };

  const handleFormTypeChange = (type) => {
    setFormType(type);
    if (type === 'default') {
      setQuestions(defaultQuestions);
    } else {
      setQuestions([]);
    }
  };

  const addQuestion = () => {
    if (questions.length >= 20) {
      message.warning('Maximum 20 questions allowed per form');
      return;
    }

    const newQuestion = {
      id: Date.now(),
      text: '',
      type: formType === 'yes_no' ? 'yes_no' : 'multiple_choice',
      required: true,
      order: questions.length + 1,
      options: formType === 'yes_no' ? ['Yes', 'No'] : ['Option 1', 'Option 2']
    };
    setQuestions([...questions, newQuestion]);
  };

  const updateQuestion = (questionId, field, value) => {
    setQuestions(questions.map(q => 
      q.id === questionId ? { ...q, [field]: value } : q
    ));
  };

  const removeQuestion = (questionId) => {
    if (questions.length <= 1) {
      message.warning('At least one question is required');
      return;
    }
    setQuestions(questions.filter(q => q.id !== questionId));
  };

  const validateForm = () => {
    // Validate form name
    if (!form.getFieldValue('name')?.trim()) {
      message.error('Form name is required');
      return false;
    }

    // Validate questions
    if (questions.length === 0) {
      message.error('At least one question is required');
      return false;
    }

    // Validate each question
    for (let i = 0; i < questions.length; i++) {
      const question = questions[i];
      
      if (!question.text?.trim()) {
        message.error(`Question ${i + 1}: Question text is required`);
        return false;
      }

      if (question.type !== 'text' && (!question.options || question.options.length < 2)) {
        message.error(`Question ${i + 1}: At least 2 options are required`);
        return false;
      }

      // Check for empty options
      if (question.options) {
        const emptyOptions = question.options.filter(opt => !opt?.trim());
        if (emptyOptions.length > 0) {
          message.error(`Question ${i + 1}: All options must have text`);
          return false;
        }
      }
    }

    return true;
  };

  const handleSubmit = async (values) => {
    try {
      // Validate form before submission
      if (!validateForm()) {
        return;
      }

      // Prepare API data structure
      const apiData = {
        form_title: values.name,
        form_type: formType,
        form_questions: JSON.stringify(questions),
        course_id: values.course_id,
        created_by: 'admin' // This should come from user context
      };

      if (editingForm) {
        // Update existing form
        const response = await FeedbackFormsAPI.updateForm(editingForm.id, apiData);
        if (response.status === 'Ok' && response.statuscode === 200) {
          message.success('Feedback form updated successfully');
          loadForms(); // Reload forms from API
        } else {
          message.error(response.message || 'Failed to update feedback form');
        }
      } else {
        // Create new form
        const response = await FeedbackFormsAPI.createForm(apiData);
        if (response.status === 'Ok' && response.statuscode === 200) {
          message.success('Feedback form created successfully');
          loadForms(); // Reload forms from API
        } else {
          message.error(response.message || 'Failed to create feedback form');
        }
      }

      setModalVisible(false);
      form.resetFields();
      setQuestions([]);
      setFormType('default');
      setSelectedCourses([]);
    } catch (error) {
      console.error('Form submission error:', error);
      message.error('Failed to save feedback form. Please try again.');
    }
  };

  const handleAssignCourses = (formData) => {
    setSelectedForm(formData);
    setSelectedCourses(formData.assignedCourses || []);
    setAssignmentModalVisible(true);
  };

  const handleAssignmentSubmit = async () => {
    try {
      // TODO: Replace with actual API call
      // await axios.post(`${BaseUrl}feedback-forms/${selectedForm.id}/assign-courses`, 
      //   { courseIds: selectedCourses }, 
      //   { withCredentials: true }
      // );
      
      // Mock assignment - replace with actual API call
      setForms(forms.map(f => 
        f.id === selectedForm.id 
          ? { ...f, assignedCourses: selectedCourses }
          : f
      ));
      message.success('Courses assigned successfully');
      setAssignmentModalVisible(false);
    } catch (error) {
      console.error('Error assigning courses:', error);
      message.error('Failed to assign courses. Please try again.');
    }
  };

  const handleViewReports = async (courseId) => {
    try {
      const response = await FeedbackFormsAPI.getCourseReports(courseId);
      if (response.status === 'Ok' && response.statuscode === 200) {
        setCourseReports(response.data || []);
        setReportsModalVisible(true);
      } else {
        message.error(response.message || 'Failed to load course reports');
      }
    } catch (error) {
      console.error('Error loading course reports:', error);
      message.error('Failed to load course reports. Please try again.');
    }
  };

  const filteredForms = forms.filter(form => {
    if (filters.course && !form.assignedCourses?.includes(parseInt(filters.course))) {
      return false;
    }
    if (filters.formType && form.type !== filters.formType) {
      return false;
    }
    if (filters.dateRange && filters.dateRange.length === 2) {
      const formDate = moment(form.createdAt);
      if (!formDate.isBetween(filters.dateRange[0], filters.dateRange[1], 'day', '[]')) {
        return false;
      }
    }
    return true;
  });

  console.log('Current forms state:', forms);
  console.log('Filtered forms:', filteredForms);

  return (
    <div className="feedback-forms-main">
      <div className="feedback-forms-header">
        <Title level={2}>Feedback Forms</Title>
        <Space>
          <Button 
            icon={<FilterOutlined />}
            onClick={() => navigate('/feedbackreports')}
          >
            View Reports
          </Button>
          <Button 
            type="primary" 
            icon={<PlusOutlined />}
            onClick={handleCreateForm}
            size="large"
          >
            Create Feedback Form
          </Button>
        </Space>
      </div>

      {/* Filters */}
      <Card className="filters-card">
        <Row gutter={16}>
          <Col xs={24} sm={8} md={6}>
            <Select
              placeholder="Filter by Course"
              allowClear
              showSearch
              style={{ width: '100%' }}
              value={filters.course}
              onChange={(value) => setFilters({ ...filters, course: value })}
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
              }
            >
              {courses.map(course => (
                <Option key={course.course_id} value={course.course_id}>
                  {course.course_title}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={8} md={6}>
            <Select
              placeholder="Filter by Form Type"
              allowClear
              showSearch
              style={{ width: '100%' }}
              value={filters.formType}
              onChange={(value) => setFilters({ ...filters, formType: value })}
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
              }
            >
              {formTypes.map(type => (
                <Option key={type.value} value={type.value}>
                  {type.label}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={8} md={6}>
            <DatePicker.RangePicker
              placeholder={['Start Date', 'End Date']}
              style={{ width: '100%' }}
              value={filters.dateRange}
              onChange={(dates) => setFilters({ ...filters, dateRange: dates })}
            />
          </Col>
          <Col xs={24} sm={24} md={6}>
            <Button 
              icon={<FilterOutlined />}
              onClick={() => setFilters({ course: '', formType: '', dateRange: null })}
            >
              Clear Filters
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Forms Table */}
      <Card>
        <Spin spinning={loading}>
          <Table
            columns={columns}
            dataSource={filteredForms}
            rowKey="id"
            pagination={{
              pageSize: 10,
              showSizeChanger: false,
              showQuickJumper: true,
              showTotal: (total, range) => 
                `${range[0]}-${range[1]} of ${total} forms`
            }}
            locale={{
              emptyText: (
                <Empty
                  description="No feedback forms found"
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              )
            }}
          />
        </Spin>
      </Card>

      {/* Create/Edit Form Modal */}
      <Modal
        title={editingForm ? 'Edit Feedback Form' : 'Create Feedback Form'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        width={800}
        footer={null}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="name"
            label="Form Name"
            rules={[{ required: true, message: 'Please enter form name' }]}
          >
            <Input placeholder="Enter feedback form name" />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
          >
            <TextArea 
              rows={3} 
              placeholder="Enter form description (optional)" 
            />
          </Form.Item>

          <Form.Item
            name="course_id"
            label="Assign to Course"
            rules={[{ required: true, message: 'Please select a course' }]}
          >
            <Select
              placeholder="Select a course"
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={courses.map(course => ({
                label: course.course_title,
                value: course.course_id
              }))}
            />
          </Form.Item>

          <Form.Item label="Form Type">
            <Radio.Group 
              value={formType} 
              onChange={(e) => handleFormTypeChange(e.target.value)}
            >
              {formTypes.map(type => (
                <Radio key={type.value} value={type.value}>
                  <div>
                    <Text strong>{type.label}</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      {type.description}
                    </Text>
                  </div>
                </Radio>
              ))}
            </Radio.Group>
          </Form.Item>

          <Divider>Questions</Divider>

          {questions && questions.length > 0 ? questions.map((question, index) => (
            <Card 
              key={question.id} 
              size="small" 
              className="question-card"
              title={`Question ${index + 1}`}
              extra={
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => removeQuestion(question.id)}
                />
              }
            >
              <Form.Item label="Question Text">
                <Input
                  value={question.text}
                  onChange={(e) => updateQuestion(question.id, 'text', e.target.value)}
                  placeholder="Enter your question"
                />
              </Form.Item>

              {question.type !== 'text' && (
                <Form.Item label="Options">
                  {question.options && question.options.map((option, optIndex) => (
                    <div key={optIndex} style={{ display: 'flex', marginBottom: 8, gap: 8 }}>
                      <Input
                        value={option}
                        onChange={(e) => {
                          const newOptions = [...question.options];
                          newOptions[optIndex] = e.target.value;
                          updateQuestion(question.id, 'options', newOptions);
                        }}
                        placeholder={`Option ${optIndex + 1}`}
                        style={{ flex: 1 }}
                      />
                      {question.options && question.options.length > 2 && (
                        <Button
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => {
                            const newOptions = question.options.filter((_, i) => i !== optIndex);
                            updateQuestion(question.id, 'options', newOptions);
                          }}
                          title="Remove option"
                        />
                      )}
                    </div>
                  ))}
                  <Button
                    type="dashed"
                    onClick={() => {
                      if (question.options && question.options.length >= 10) {
                        message.warning('Maximum 10 options allowed per question');
                        return;
                      }
                      const currentOptions = question.options || [];
                      const newOptions = [...currentOptions, `Option ${currentOptions.length + 1}`];
                      updateQuestion(question.id, 'options', newOptions);
                    }}
                    style={{ width: '100%' }}
                    disabled={question.options && question.options.length >= 10}
                  >
                    Add Option {question.options && question.options.length >= 10 ? '(Max 10)' : ''}
                  </Button>
                </Form.Item>
              )}

              <Form.Item>
                <Checkbox
                  checked={question.required}
                  onChange={(e) => updateQuestion(question.id, 'required', e.target.checked)}
                >
                  Required question
                </Checkbox>
              </Form.Item>
            </Card>
          )) : <Empty description="No questions added yet. Click 'Add Question' to get started." />}

          <Button
            type="dashed"
            onClick={addQuestion}
            icon={<PlusOutlined />}
            style={{ width: '100%', marginBottom: 16 }}
          >
            Add Question
          </Button>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingForm ? 'Update Form' : 'Create Form'}
              </Button>
              <Button onClick={() => setModalVisible(false)}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Preview Modal */}
      <Modal
        title="Form Preview"
        open={previewModalVisible}
        onCancel={() => setPreviewModalVisible(false)}
        width={600}
        footer={[
          // <Button key="assign" type="primary" onClick={() => handleAssignCourses(selectedForm)}>
          //   Assign to Courses
          // </Button>,
          <Button key="close" onClick={() => setPreviewModalVisible(false)}>
            Close
          </Button>
        ]}
      >
        {selectedForm && (
          <div>
            <Title level={3}>{selectedForm.name}</Title>
            {selectedForm.description && (
              <Text type="secondary">{selectedForm.description}</Text>
            )}
            <Divider />
            
            <Form form={previewForm} layout="vertical">
              {selectedForm.questions && selectedForm.questions.length > 0 ? (
                selectedForm.questions.map((question, index) => (
                  <Form.Item
                    key={question.id}
                    label={`${index + 1}. ${question.text} ${question.required ? '*' : ''}`}
                    rules={question.required ? [{ required: true, message: 'This field is required' }] : []}
                  >
                    {question.type === 'text' && (
                      <TextArea rows={3} placeholder="Your answer..." />
                    )}
                    {question.type === 'rating' && question.options && (
                      <Radio.Group>
                        {question.options.map((option, optIndex) => (
                          <Radio key={optIndex} value={optIndex + 1}>
                            {option}
                          </Radio>
                        ))}
                      </Radio.Group>
                    )}
                    {question.type === 'multiple_choice' && question.options && (
                      <Radio.Group>
                        {question.options.map((option, optIndex) => (
                          <Radio key={optIndex} value={option}>
                            {option}
                          </Radio>
                        ))}
                      </Radio.Group>
                    )}
                    {question.type === 'yes_no' && (
                      <Radio.Group>
                        <Radio value="yes">Yes</Radio>
                        <Radio value="no">No</Radio>
                      </Radio.Group>
                    )}
                  </Form.Item>
                ))
              ) : (
                <Empty description="No questions available" />
              )}
            </Form>
          </div>
        )}
      </Modal>

      {/* Course Assignment Modal */}
      <Modal
        title="Assign Form to Courses"
        open={assignmentModalVisible}
        onOk={handleAssignmentSubmit}
        onCancel={() => setAssignmentModalVisible(false)}
        width={500}
      >
        <div>
          <Text>Select courses to assign "{selectedForm?.name}" to:</Text>
          <Divider />
          <Checkbox.Group
            value={selectedCourses}
            onChange={setSelectedCourses}
            style={{ width: '100%' }}
          >
            <Row>
              {courses.map(course => (
                <Col span={24} key={course.course_id} style={{ marginBottom: 8 }}>
                  <Checkbox value={course.course_id}>
                    {course.course_title}
                  </Checkbox>
                </Col>
              ))}
            </Row>
          </Checkbox.Group>
        </div>
      </Modal>

      {/* Course Reports Modal */}
      <Modal
        title="Course Feedback Reports"
        open={reportsModalVisible}
        onCancel={() => setReportsModalVisible(false)}
        width={800}
        footer={[
          <Button key="close" onClick={() => setReportsModalVisible(false)}>
            Close
          </Button>
        ]}
      >
        <div>
          {courseReports.length > 0 ? (
            <Table
              dataSource={courseReports}
              rowKey="id"
              pagination={{ pageSize: 10 }}
              columns={[
                {
                  title: 'Form Name',
                  dataIndex: 'form_title',
                  key: 'form_title',
                },
                {
                  title: 'Response Count',
                  dataIndex: 'response_count',
                  key: 'response_count',
                },
                {
                  title: 'Average Rating',
                  dataIndex: 'average_rating',
                  key: 'average_rating',
                  render: (rating) => rating ? `${rating.toFixed(1)}/5` : 'N/A'
                },
                {
                  title: 'Last Response',
                  dataIndex: 'last_response_date',
                  key: 'last_response_date',
                  render: (date) => date ? moment(date).format('MMM DD, YYYY') : 'N/A'
                }
              ]}
            />
          ) : (
            <Empty description="No reports available for this course" />
          )}
        </div>
      </Modal>
    </div>
  );
};

export default FeedbackForms;
